/**
 * Registry ledger status — the wire contract for GET /api/registry (metrics
 * substrate S5). The registry CANON (atoms; cards were wiped by #740) ships in the bundle
 * (registry.json), so the glass renders it with zero fetch; this endpoint
 * carries only the LIVE ledger state D1 holds: how many atoms of each type
 * have landed (first sightings, metric_atoms), the newest event_date per type,
 * each type's dated revisions (metric_atom_revisions, migration 0020) and its
 * latest state, and the most recent extraction run marker. Read-only — no
 * write path, no OPS-MCP contract marker.
 *
 * Latest state (#1180): the rule is apps/worker/src/atoms/extract.ts
 * latestState (design #1139 §3, ruled #1176); an atom whose latest recorded
 * value is null is not counted in latestStateCount.
 */

import { z } from "zod";

/** One atom type's landed footprint in the ledger. */
export const RegistryAtomLedgerStatusSchema = z.object({
  atomType: z.string(),
  /** First sightings: metric_atoms rows of this type. */
  count: z.number().int().nonnegative(),
  /** Newest Pacific event_date landed for this type, or null if none yet. */
  latestEventDate: z.string().nullable(),
  /** metric_atom_revisions rows of this type. */
  revisionCount: z.number().int().nonnegative(),
  /** Newest revision day (the detection day, a revision's event_date), or
   *  null when this type has no revision. */
  latestRevisionDay: z.string().nullable(),
  /** Atoms of this type whose latest recorded value exists, any day. */
  latestStateCount: z.number().int().nonnegative(),
  /** Of latestStateCount, those whose latest value is dated before the
   *  ledger's ship day (ledgerShip.on) — a re-dated stamp moved before it. */
  latestStateBeforeShip: z.number().int().nonnegative(),
  /** Newest Pacific day among the latest values, or null when none exists. */
  latestStateLatestEventDate: z.string().nullable(),
});

/** The most recent extraction run marker (atom_extraction_runs). */
export const RegistryRunMarkerSchema = z.object({
  dayAnchor: z.string(),
  ranAt: z.string(),
  atomsInserted: z.number().int().nonnegative(),
});

export const RegistryLedgerStatusResponseSchema = z.object({
  /** Per-atom-type counts, one row per type that has landed at least once. */
  atoms: z.array(RegistryAtomLedgerStatusSchema),
  /** The newest run marker, or null before the extractor has ever run. */
  latestRun: RegistryRunMarkerSchema.nullable(),
  /** The ledger's first day — the Contract's provenance entry
   *  (engineering/contract/provenance.ts atom_ledger_ship), carried by the
   *  route so the glass names the rule beside every atom the ship day gates. */
  ledgerShip: z.object({ id: z.string(), on: z.string() }),
  /** The revisions' story by pointer — the Contract's provenance entry id
   *  (provenance.ts atom_revisions_ship). Its id only: the entry's day is not
   *  read by any code (provenance.ts, the entry's rationale). */
  revisionsShip: z.object({ id: z.string() }),
  /** An atom's OWN first day where registry.json names one (when.provenance):
   *  the entry's id and day, resolved by the route from the Contract so the
   *  glass renders the day beside the atom without a copy of it anywhere. */
  atomBoundaries: z.array(
    z.object({ atomType: z.string(), id: z.string(), on: z.string() }),
  ),
});

export type RegistryAtomLedgerStatus = z.infer<
  typeof RegistryAtomLedgerStatusSchema
>;
export type RegistryRunMarker = z.infer<typeof RegistryRunMarkerSchema>;
export type RegistryLedgerStatusResponse = z.infer<
  typeof RegistryLedgerStatusResponseSchema
>;
